import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Bell, Calendar, Users, AlertTriangle, Mail, Check, Clock, X, ChevronRight } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Institution Message Log / Admin View
 * 
 * Shows message history with:
 * - Message type
 * - Sender
 * - Time sent
 * - Recipient count
 * - Delivery status summary
 * 
 * Supports transparency and moderation if problems occur.
 */

const MESSAGE_TYPE_CONFIG = {
  announcement: { label: 'Announcement', icon: Bell, color: 'var(--lk-sage)' },
  reminder: { label: 'Reminder', icon: Calendar, color: 'var(--lk-earth)' },
  coordination: { label: 'Coordination', icon: Users, color: 'var(--lk-secondary)' },
  urgent_notice: { label: 'Urgent Notice', icon: AlertTriangle, color: '#D97706' }
};

const STATUS_COLORS = {
  sent: { bg: '#F0FDF4', text: '#16A34A' },
  queued: { bg: '#FEF3C7', text: '#D97706' },
  failed: { bg: '#FEF2F2', text: '#DC2626' },
  pending_confirmation: { bg: '#F3F4F6', text: '#6B7280' }
};

export default function MessageLog() {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [institution, setInstitution] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    fetchData();
  }, [institutionId, filter]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch institution
      const instRes = await fetch(`${API}/api/inst-admin/${institutionId}/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (instRes.ok) {
        const instData = await instRes.json();
        setInstitution(instData);
      }
      
      // Fetch message log
      let url = `${API}/api/institution-messages/institutions/${institutionId}/message-log?limit=100`;
      const logRes = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (logRes.ok) {
        const logData = await logRes.json();
        let filteredMessages = logData.log_entries || [];
        
        if (filter !== 'all') {
          filteredMessages = filteredMessages.filter(m => m.message_type === filter);
        }
        
        setMessages(filteredMessages);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (isoString) => {
    if (!isoString) return 'Not sent';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  
  const getDeliveryLabel = (summary) => {
    if (!summary) return 'Unknown';
    const { sent = 0, failed = 0, queued = 0 } = summary;
    
    if (failed > 0) return `${sent} sent, ${failed} failed`;
    if (queued > 0) return `${sent} sent, ${queued} queued`;
    return `${sent} delivered`;
  };
  
  if (loading && !institution) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate(`/institutions/${institutionId}`)} className="flex items-center gap-2 hover:opacity-70" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Message History</h1>
            <Link 
              to={`/institutions/${institutionId}/compose`}
              className="p-2 rounded-lg hover:opacity-70"
              style={{ backgroundColor: 'var(--lk-sage)' }}
              data-testid="compose-button"
            >
              <Plus className="w-5 h-5 text-white" />
            </Link>
          </div>
        </div>
      </header>
      
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Institution Name */}
        {institution && (
          <div className="mb-4">
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              {institution.name}
            </p>
          </div>
        )}
        
        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-4 px-4">
          <button
            onClick={() => setFilter('all')}
            className="px-4 py-2 rounded-full text-sm whitespace-nowrap"
            style={{
              backgroundColor: filter === 'all' ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
              color: filter === 'all' ? 'white' : 'var(--fy-text-secondary)'
            }}
          >
            All Messages
          </button>
          {Object.entries(MESSAGE_TYPE_CONFIG).map(([type, config]) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className="px-4 py-2 rounded-full text-sm whitespace-nowrap flex items-center gap-1"
              style={{
                backgroundColor: filter === type ? config.color : 'var(--fy-surface-muted)',
                color: filter === type ? 'white' : 'var(--fy-text-secondary)'
              }}
            >
              {config.label}
            </button>
          ))}
        </div>
        
        {/* Message List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--lk-sage)' }} />
          </div>
        ) : messages.length === 0 ? (
          <Card className="p-8 text-center" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
            <Mail className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
            <p className="font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
              No messages yet
            </p>
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Send your first coordination message to members
            </p>
            <Link to={`/institutions/${institutionId}/compose`}>
              <Button style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}>
                Compose Message
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const config = MESSAGE_TYPE_CONFIG[msg.message_type] || MESSAGE_TYPE_CONFIG.announcement;
              const Icon = config.icon;
              const statusColor = STATUS_COLORS[msg.status] || STATUS_COLORS.sent;
              
              return (
                <Link 
                  key={msg.message_id} 
                  to={`/institutions/${institutionId}/messages/${msg.message_id}`}
                  className="block"
                >
                  <Card 
                    className="p-4 hover:shadow-md transition-shadow"
                    style={{ backgroundColor: 'var(--fy-surface-card)' }}
                    data-testid={`message-${msg.message_id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Type Icon */}
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: config.color }} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Type Badge */}
                        <div className="flex items-center gap-2 mb-1">
                          <span 
                            className="text-xs px-2 py-0.5 rounded"
                            style={{ backgroundColor: `${config.color}15`, color: config.color }}
                          >
                            {config.label}
                          </span>
                          <span 
                            className="text-xs px-2 py-0.5 rounded"
                            style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                          >
                            {msg.status === 'sent' ? 'Sent' : msg.status.replace('_', ' ')}
                          </span>
                        </div>
                        
                        {/* Subject */}
                        <h3 className="font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>
                          {msg.subject}
                        </h3>
                        
                        {/* Meta */}
                        <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(msg.sent_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {msg.recipient_count} recipients
                          </span>
                        </div>
                        
                        {/* Delivery Summary */}
                        {msg.delivery_summary && (
                          <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                            {getDeliveryLabel(msg.delivery_summary)}
                          </p>
                        )}
                      </div>
                      
                      <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
